/**
 * The organization list and account page (RUT-4072): which companies each filter
 * holds, what each summary bar counts, and the money on each row.
 *
 * Status and health come from the server (`customerStatus.ts`, which reads
 * "who are we waiting on" off the same items Home's "Needs attention" shows).
 * What Ruta earns and the margin come from `GET /costs/platform`, per company
 * and month — the same figures Home adds up — never re-derived here.
 */

import type {
  HealthLevel,
  JustifiBucket,
  OrgRow,
  OverviewOrgMonth,
  OverviewResponse,
  TelnyxBucket,
  WaitingOn,
} from './types';
import { daysSince } from './businessHealth';

/** JustiFi's dashboard. Deep links are not published, so actions open it and copy the ID. */
export const JUSTIFI_PORTAL_URL = 'https://portal.justifi.tech';

/** Telnyx's Mission Control portal. Same: open it and copy the ID. */
export const TELNYX_PORTAL_URL = 'https://portal.telnyx.com';

export interface OrganizationRow {
  org: OrgRow;
  /** Card revenue through Ruta Pay this month. */
  cardRevenue: number;
  /** Everything their customers paid this month, card or not. */
  collected: number;
  /** The plan fee on the newest invoice we sent; the plan rule only for a company never invoiced. */
  fee: number | null;
  feeWithRutaPay: boolean | null;
  /** Invoiced plan revenue + Ruta Pay spread; null until the money figures load. */
  toRuta: number | null;
  /** To Ruta less the direct costs they caused; null until loaded. */
  subscriptionMargin: number | null;
  /** Margin as a share of To Ruta; null without revenue. */
  marginPct: number | null;
  /** True when they have ever taken a card payment through Ruta Pay, since signup. */
  hadCardRevenue: boolean;
  /** Whether Home counts them this month; null until Home's figures load. */
  countedOnHome: boolean | null;
}

/** One company's money for one month, from Home's endpoint. */
export function moneyFor(
  overview: OverviewResponse | undefined,
  orgId: string,
  month: string,
): OverviewOrgMonth | null {
  const org = overview?.byOrg.find((o) => o.orgId === orgId);
  return org?.byMonth.find((m) => m.month === month) ?? null;
}

export function marginPct(revenue: number, margin: number): number | null {
  return revenue > 0 ? margin / revenue : null;
}

export function organizationRow(org: OrgRow, month: string, overview: OverviewResponse | undefined): OrganizationRow {
  const usage = org.byMonth.find((m) => m.month === month);
  // Only a month Home counts carries money here, so the row adds up to Home.
  const found = moneyFor(overview, org.orgId, month);
  const money = found && found.counted ? found : null;
  const notCounted = Boolean(found && !found.counted);
  return {
    org,
    cardRevenue: usage?.onlineRevenue ?? 0,
    collected: usage?.paidRevenue ?? 0,
    // What we actually charged beats what the rule says we would.
    fee: org.lastChargedFee?.amount ?? usage?.platformFee ?? null,
    feeWithRutaPay: org.lastChargedFee ? org.lastChargedFee.withRutaPay : (usage?.feeWithRutaPay ?? null),
    toRuta: money ? money.revenueToRuta : notCounted ? 0 : null,
    subscriptionMargin: money ? money.subscriptionMargin : notCounted ? 0 : null,
    countedOnHome: found ? found.counted : null,
    marginPct: money ? marginPct(money.revenueToRuta, money.subscriptionMargin) : null,
    hadCardRevenue: org.hadRutaPayEver,
  };
}

/** Rows the list shows; the demo account and companies never billed and switched off are named in a footnote instead. */
export function isListed(org: OrgRow): boolean {
  return org.status.health.level !== 'not_counted';
}

/** Companies the summary bars count: listed and not lost. */
export function isCurrent(org: OrgRow): boolean {
  return isListed(org) && org.status.health.level !== 'lost';
}

function waitingOn(org: OrgRow, who: WaitingOn): boolean {
  return org.status.attention.some((item) => item.waitingOn === who);
}

export type CustomerFilter =
  | 'all'
  | 'paying'
  | 'waiting_justifi'
  | 'waiting_customer'
  | 'telnyx_stalled'
  | 'no_card_revenue'
  | 'lost';

export const CUSTOMER_FILTERS: Array<{ key: CustomerFilter; label: string; test: (row: OrganizationRow) => boolean }> = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'paying', label: 'Paying', test: (row) => row.org.isPaying && isCurrent(row.org) },
  {
    key: 'waiting_justifi',
    label: 'Waiting on JustiFi',
    test: (row) => isCurrent(row.org) && waitingOn(row.org, 'justifi'),
  },
  {
    key: 'waiting_customer',
    label: 'Waiting on the organization',
    test: (row) => isCurrent(row.org) && waitingOn(row.org, 'customer'),
  },
  {
    key: 'telnyx_stalled',
    label: 'Telnyx stalled',
    test: (row) => isCurrent(row.org) && row.org.status.telnyx.waitingOn !== null,
  },
  {
    key: 'no_card_revenue',
    label: 'No card revenue yet',
    test: (row) => isCurrent(row.org) && !row.hadCardRevenue,
  },
  { key: 'lost', label: 'Lost', test: (row) => row.org.status.health.level === 'lost' },
];

/** A filter key from the address bar, or 'all' when it is not one we know. */
export function parseFilter(value: string | null): CustomerFilter {
  return CUSTOMER_FILTERS.some((f) => f.key === value) ? (value as CustomerFilter) : 'all';
}

export function filterRows(rows: OrganizationRow[], filter: CustomerFilter): OrganizationRow[] {
  const def = CUSTOMER_FILTERS.find((f) => f.key === filter) ?? CUSTOMER_FILTERS[0];
  return rows.filter(def.test);
}

/** Company name, owner email, slug or company ID. */
export function matchesSearch(org: OrgRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [org.name, org.contactEmail, org.slug, org.orgId].some((v) => v?.toLowerCase().includes(q));
}

const HEALTH_ORDER: Record<HealthLevel, number> = {
  at_risk: 0,
  watch: 1,
  setting_up: 2,
  healthy: 3,
  lost: 4,
  not_counted: 5,
};

/** Who needs us first: at risk, then watch, then the rest; biggest earners first within each. */
export function sortRows(rows: OrganizationRow[]): OrganizationRow[] {
  return [...rows].sort((a, b) => {
    const byHealth = HEALTH_ORDER[a.org.status.health.level] - HEALTH_ORDER[b.org.status.health.level];
    if (byHealth !== 0) return byHealth;
    const byMoney = (b.toRuta ?? 0) - (a.toRuta ?? 0);
    if (byMoney !== 0) return byMoney;
    return a.org.name.localeCompare(b.org.name);
  });
}

export interface BucketDef<B extends string> {
  bucket: B;
  label: string;
  /** Tailwind background for the bar segment and its swatch. */
  tone: string;
}

export const JUSTIFI_BUCKETS: BucketDef<JustifiBucket>[] = [
  { bucket: 'live', label: 'Live', tone: 'bg-primary' },
  { bucket: 'approved', label: 'Approved, no card payment yet', tone: 'bg-primary/40' },
  { bucket: 'in_review', label: 'In review at JustiFi', tone: 'bg-stone-400' },
  { bucket: 'needs_info', label: 'JustiFi needs info', tone: 'bg-orange-500' },
  { bucket: 'rejected', label: 'Rejected', tone: 'bg-red-600' },
  { bucket: 'not_connected', label: 'Not connected', tone: 'bg-stone-200' },
];

export const TELNYX_BUCKETS: BucketDef<TelnyxBucket>[] = [
  { bucket: 'own_brand_approved', label: 'Own brand approved', tone: 'bg-primary' },
  { bucket: 'ruta_brand_campaign', label: "Campaign under Ruta's brand", tone: 'bg-primary/40' },
  { bucket: 'campaign_review', label: 'Campaign in review', tone: 'bg-stone-400' },
  { bucket: 'brand_review', label: 'Brand in review', tone: 'bg-stone-300' },
  { bucket: 'to_file', label: 'Ready to file, not filed', tone: 'bg-orange-500' },
  { bucket: 'rejected', label: 'Rejected', tone: 'bg-red-600' },
  { bucket: 'not_started', label: 'Not started', tone: 'bg-stone-200' },
];

/** How many companies sit in each bucket, in the order given. */
export function bucketCounts<B extends string>(
  orgs: OrgRow[],
  buckets: BucketDef<B>[],
  pick: (org: OrgRow) => B,
): Array<BucketDef<B> & { count: number }> {
  return buckets.map((def) => ({ ...def, count: orgs.filter((org) => pick(org) === def.bucket).length }));
}

/** "Today", "1 day", "9 days", "3 months" — how long a status has held. */
export function durationLabel(since: string | null, now: Date): string | null {
  const days = daysSince(since, now);
  if (days === null) return null;
  if (days === 0) return 'Today';
  if (days < 60) return `${days} ${days === 1 ? 'day' : 'days'}`;
  return `${Math.floor(days / 30)} months`;
}

export const WAITING_ON_SHORT: Record<WaitingOn, string> = {
  customer: 'waiting on them',
  justifi: 'waiting on JustiFi',
  telnyx: 'waiting on Telnyx',
  us: 'waiting on us',
};

/** The sub-line under a status: how long it has held, and who has to move when someone does. */
export function statusSubline(
  status: { since: string | null; waitingOn: WaitingOn | null },
  now: Date,
): string | null {
  const parts = [
    durationLabel(status.since, now),
    status.waitingOn ? WAITING_ON_SHORT[status.waitingOn] : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The last month that has finished, or the only month when there is just one. */
export function lastCompleteMonth(months: string[], partialMonth: string | undefined): string {
  const complete = months.filter((m) => m !== partialMonth);
  return complete[complete.length - 1] ?? months[months.length - 1];
}
